import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useTranslation } from 'react-i18next';

export interface EmptyStateProps {
  readonly title?: string;
  readonly description?: string;
  /** A single next step — creating the missing thing, or clearing the filter that hid it. */
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
  readonly compact?: boolean;
  /**
   * Announce the state to assistive tech via `role="status"`. Default true.
   *
   * Set false when rendering INSIDE a `role="grid"` (a Data Grid's no-rows overlay): a grid may
   * only contain row/rowgroup children, so a status role there is an aria-required-children
   * violation. The grid already conveys emptiness through `aria-rowcount`.
   */
  readonly announce?: boolean;
}

/**
 * "Nothing here" is a designed state, not an accident. Shipping it as a pattern is what stops
 * each screen inventing its own blank area (backbone §6, story-first rule).
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  compact = false,
  announce = true,
}: EmptyStateProps): ReactNode {
  const { t } = useTranslation('patterns');
  return (
    <Stack
      spacing={1}
      role={announce ? 'status' : undefined}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: compact ? 3 : 6,
        px: 2,
      }}
    >
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>
        {icon ?? <InboxOutlinedIcon fontSize="large" />}
      </Box>
      <Typography variant="h4" component="p">
        {title ?? t('empty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
        {description ?? t('empty.description')}
      </Typography>
      {action !== undefined && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}
